import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { generate } from "shortid";

import {
  postApplicantUploadComplete,
  postApplicantUploadInit,
  postApplicantUploadPresigned,
} from "@api/applicants/applicantFileUpload";
import { ApiResponse, put } from "@api/httpClient";

import { useGlobalFileUploadConfig } from "@shared/hooks/query";
import { dayjs } from "@shared/services/date";
import {
  FileChunkUpload,
  FileUpload,
  FileUploadStatus,
  getFileChunks,
} from "@shared/services/fileUpload";

interface UseApplicantFileUploadProps {
  applicantExamId: number;
  resultId: number;
}

const MAX_CONCURRENT_UPLOADS = 4;
const S3_PRESIGNED_URL_EXPIRY_MINUTES = 15;

/**
 * Handles multi part file upload to s3
 * https://www.notion.so/givery/File-Upload-Restrictions-a21c77c281d14929b5adc88a43aa208e
 */
export const useApplicantFileUpload = (params: UseApplicantFileUploadProps) => {
  const {
    data: { uploadPartMaxSizeBytes },
  } = useGlobalFileUploadConfig();
  const [filesToUpload, setFilesToUpload] = useState<FileUpload[]>([]);

  const [uploadingFile, setUploadingFile] = useState<FileUpload | null>(null);

  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      filesToUpload.forEach((file) => file.abortController.abort());
    };
  }, []);

  const uploadFiles = useCallback(
    (files: File[]) => {
      if (!files.length) {
        return;
      }

      const newFiles = files.map(
        (file) => {
          const abortController = new AbortController();
          const fileId = generate();
          const fileChunks = getFileChunks({
            fileId,
            file,
            abortController,
            maxChunkSize: uploadPartMaxSizeBytes,
          });

          return {
            id: fileId,
            status: FileUploadStatus.NOT_STARTED,
            file,
            abortController,
            chunks: fileChunks,
            uploadedCount: 0,
          };
        },
        {} as { [fileName: string]: FileUpload },
      );

      // abort all currently uploading files that have the same
      // file name as the new files
      newFiles.forEach((newFile) => {
        const uploadingFile = filesToUpload.find(
          (file) =>
            file.file.name === newFile.file.name &&
            file.status === FileUploadStatus.UPLOADING,
        );

        if (uploadingFile) {
          uploadingFile.abortController.abort();
        }
      });

      // replace existing uploads that use the same file names
      setFilesToUpload((oldFiles) => {
        const uniqueFilesByName = [...newFiles, ...oldFiles].reduce(
          (arr, file) => {
            if (!arr[file.file.name]) {
              arr[file.file.name] = file;
            }

            return arr;
          },
          {},
        );

        return Object.values(uniqueFilesByName);
      });
    },
    [filesToUpload, uploadPartMaxSizeBytes],
  );

  const { mutate: fileUploadMutate } = useMutation({
    mutationFn: async (file: FileUpload) => {
      const { applicantExamId, resultId } = params;
      const signal = file.abortController.signal;
      const filename = file.file.name;

      const initResult = await postApplicantUploadInit({
        applicantExamId,
        resultId,
        signal,
        payload: {
          filename,
        },
      }).catch((): false => false);

      if (!isMounted.current) {
        return;
      }

      if (!initResult) {
        setFileAsFailed(file.id);
        return;
      }

      const uploadId = initResult.result.uploadId;

      const completedChunks: FileChunkUpload[] = [];

      for (let x = 0; x < file.chunks.length; x += MAX_CONCURRENT_UPLOADS) {
        const chunksToUpload = file.chunks.slice(x, x + MAX_CONCURRENT_UPLOADS); // Get a block of 4 chunks
        const uploadPromises = chunksToUpload.map((chunk) =>
          uploadToS3(chunk, uploadId),
        );

        try {
          // if any promise rejections occur, this will return immediately
          const updatedChunks = await Promise.all(uploadPromises);

          completedChunks.push(...updatedChunks);
        } catch (error) {
          if (!isMounted.current) {
            return;
          }
          file.abortController.abort();
          setFileAsFailed(file.id);
          break;
        }
      }

      if (completedChunks.length !== file.chunks.length) {
        return;
      }

      const completeResult = await postApplicantUploadComplete({
        applicantExamId,
        resultId,
        payload: {
          filename,
          uploadId,
          parts: completedChunks.map((chunk) => ({
            partNumber: chunk.partNumber,
            eTag: chunk.eTag ?? "x",
          })),
        },
        signal,
      }).catch((): false => false);

      if (!isMounted.current) {
        return;
      }

      if (!completeResult) {
        setFileAsFailed(file.id);
        return;
      }

      setFilesToUpload((files) =>
        files.map((oldFile) => {
          if (oldFile.id === file.id) {
            return {
              ...oldFile,
              status: FileUploadStatus.COMPLETE,
            };
          }

          return oldFile;
        }),
      );
      setUploadingFile(null);
    },
  });

  useEffect(() => {
    if (!filesToUpload.length || uploadingFile) {
      return;
    }

    const notStartedFile = filesToUpload.find(
      (file) => file.status === FileUploadStatus.NOT_STARTED,
    );

    if (!notStartedFile) {
      return;
    }

    setUploadingFile(notStartedFile);
    setFilesToUpload((files) =>
      files.map((file) => {
        if (file.id === notStartedFile.id) {
          return {
            ...file,
            status: FileUploadStatus.UPLOADING,
          };
        }

        return file;
      }),
    );

    fileUploadMutate(notStartedFile);
  }, [filesToUpload, uploadingFile, fileUploadMutate]);

  const uploadToS3 = async (chunk: FileChunkUpload, uploadId: string) => {
    const { applicantExamId, resultId } = params;

    return new Promise<FileChunkUpload>(async (resolve, reject) => {
      const {
        filename,
        partNumber,
        abortController,
        chunk: chunkBuffer,
      } = chunk;

      // get presigned url to upload the chunk to
      // Note: The presigned url has a 15 minute default expiration time
      const presignedResult = await postApplicantUploadPresigned({
        applicantExamId,
        resultId,
        signal: abortController.signal,
        payload: {
          contentLengthBytes: chunkBuffer.size,
          filename,
          partNumber,
          uploadId,
        },
      });

      const { url: s3Url } = presignedResult.result;

      let attempts = 0;
      const startTime = dayjs();

      const hasExpired = () => {
        return (
          dayjs(startTime).diff(dayjs(), "minute") >=
          S3_PRESIGNED_URL_EXPIRY_MINUTES
        );
      };

      // successful s3 uploads will have an etag response header
      let eTag = "";

      while (attempts < 3 && !hasExpired() && isMounted.current) {
        attempts++;

        const s3UploadResult = await put<ApiResponse<string>>(s3Url, {
          data: chunkBuffer,
          signal: abortController.signal,
        }).catch((error) => {
          if (axios.isCancel(error)) {
            return { retry: false };
          }

          return { retry: true };
        });

        if ("retry" in s3UploadResult && !s3UploadResult.retry) {
          break;
        }

        if ("headers" in s3UploadResult && s3UploadResult.headers.etag) {
          // the eTag has this format ""d07ad16eed530679c51274c99e6aba5f""
          // there are extra double quotes for some reason
          eTag = s3UploadResult.headers.etag.replaceAll('"', "");
          break;
        }
      }

      if (!eTag) {
        reject();
        return;
      }

      resolve({
        ...chunk,
        status: FileUploadStatus.COMPLETE,
        eTag,
      });
    });
  };

  const setFileAsFailed = (fileId: string) => {
    setFilesToUpload((files) =>
      files.map((file) => {
        if (file.id === fileId && file.status !== FileUploadStatus.FAILED) {
          return {
            ...file,
            status: FileUploadStatus.FAILED,
          };
        }

        return file;
      }),
    );
    setUploadingFile(null);
  };

  const cancelFileUploads = useCallback(
    (fileIds: string[]) => {
      const filesToCancel = filesToUpload.filter((file) =>
        fileIds.some((fileId) => file.id === fileId),
      );

      if (!filesToCancel.length) {
        return;
      }

      filesToCancel.forEach((file) => file.abortController.abort());

      setFilesToUpload((files) =>
        files.map((file) => {
          const shouldUpdate = fileIds.some((fileId) => file.id === fileId);
          if (shouldUpdate) {
            return {
              ...file,
              status: FileUploadStatus.CANCELLED,
            };
          }

          return file;
        }),
      );

      setUploadingFile((uploadingFile) => {
        const shouldRemoveUploadingFile = filesToCancel.some(
          (file) => file.id === uploadingFile?.id,
        );

        if (shouldRemoveUploadingFile) {
          uploadingFile?.abortController.abort();
          return null;
        }

        return uploadingFile;
      });
    },
    [filesToUpload],
  );

  const removeFileUploads = useCallback(
    (fileNames: string[]) => {
      const [keepFiles, filesToRemove] = filesToUpload.reduce(
        (acc, item) => {
          acc[fileNames.indexOf(item.file.name) === -1 ? 0 : 1].push(item);
          return acc;
        },
        [[], []] as [FileUpload[], FileUpload[]],
      );

      filesToRemove.forEach((file) => file.abortController.abort());

      setFilesToUpload(keepFiles);
    },
    [filesToUpload],
  );

  return {
    uploadFiles,
    cancelFileUploads,
    removeFileUploads,
    files: filesToUpload,
    isLoading: filesToUpload.some(
      (file) => file.status === FileUploadStatus.UPLOADING,
    ),
  };
};
